/**
 * FlashBook's bundled hands.
 *
 * A themed book is supposed to be *written*, not set: an exercise book with a
 * ruler-straight sans in it is a form with lines drawn behind it. Until now the
 * classroom theme asked the platform for a handwriting face — a stack like
 * `Bradley Hand, Segoe Script, Chalkboard SE, cursive` — which means the writing
 * changed with the machine it was read on, and sometimes quietly vanished into
 * the generic `cursive` or the system font. Nothing about a lesson should depend
 * on which laptop the learner opened it on.
 *
 * So the faces ship with the app. Two SIL Open Font License families (see
 * `assets/fonts/flashbook-hand/README.md`) carry the two scripts:
 *
 *   `FlashBookHand-*`        Latin — the classroom hand
 *   `FlashBookHandArabic-*`  Arabic — a ruqaa hand, the everyday written script
 *
 * …one family per script *and* per weight, because that is the one naming that
 * resolves everywhere: Android finds a font by its file name in the asset
 * bundle, iOS by its internal family name, and the web by whatever family the
 * stylesheet declares. The files are named, and their internal names rewritten,
 * to match.
 *
 * The stack is per-platform on purpose. On the web a family list is a per-glyph
 * contract: the browser reaches for `FlashBookHand-*` for Latin letters, slides
 * to `FlashBookHandArabic-*` for Arabic, and only if neither of *those* is there
 * yet falls back to the machine's own hand. On iOS and Android a comma-separated
 * list is not a fallback — it is a family name that fails to resolve, and the
 * text silently drops to the system font — so native names exactly one family.
 *
 * That one family has to be the right one, which is what `flashBookHandIn` is
 * for. The bundled Latin faces carry no Arabic at all (the app's own copies of
 * them were measured: zero Arabic code points between them), so a device handed
 * `FlashBookHand-Regular` for an Arabic sentence drew that sentence in the
 * system font — the one thing shipping a hand was supposed to stop. The Arabic
 * faces carry *both* scripts (an exercise quotes as well as writes), so a
 * right-to-left run names them and keeps its English words in the same hand.
 */
import {useFonts} from 'expo-font'

import {type FlashBookDirection} from '#/lib/flashbookDirection'
import {IS_WEB} from '#/env'

/** The Latin hand, by the font weight a role asked for. */
const LATIN = {
  regular: 'FlashBookHand-Regular',
  bold: 'FlashBookHand-Bold',
} as const

/**
 * The Arabic hand, by weight.
 *
 * What `flashBookHandIn` reaches for when the writing runs right to left, and
 * exported so a surface that already knows its text is Arabic — a translated
 * page, a fixed Arabic label inside a widget — can say so directly instead of
 * being handed the Latin family and hoping the platform substitutes.
 */
export const FLASHBOOK_ARABIC_HANDS = {
  regular: 'FlashBookHandArabic-Regular',
  bold: 'FlashBookHandArabic-Bold',
} as const

/**
 * How tall the bundled hand's glyphs are, as a multiple of the type size.
 *
 * The Latin faces in the bundle reach 1.266 em from their lowest outline to
 * their highest in the regular and 1.312 em in the bold — ordinary for script,
 * and *wider than the leading some themes ask for*. It matters wherever a line
 * box clips its content — `numberOfLines` clamps by clipping — because a box
 * only as tall as its leading then shaves the bottom of every descender. The
 * classroom theme sets 21px of hand inside a 26px rule, deliberately, so that
 * writing closes on the ruled line; its descenders need the remaining 4px to
 * hang in.
 *
 * Rounded up from the measured ink, and only by a little. The type suite reads
 * the shipped files themselves and fails if this stops covering them (see
 * `__tests__/fontFaces.ts`), so a re-subset or a restyle that draws taller is
 * caught rather than clipped.
 */
export const FLASHBOOK_HAND_INK_RATIO = 1.4

/**
 * The same measurement for the Arabic hand, which draws taller.
 *
 * Its outlines reach 1.407 em in the regular and 1.431 em in the bold (yMin
 * −379 to yMax 1028 of 1000 units per em at the widest), because ruqaa carries
 * its letters below a flat baseline and its dots above them. Using the Latin
 * ratio for it would leave a clamped Arabic heading about a pixel short of its
 * own ink at 21px and worse as the type grows, which is the clipping these
 * numbers exist to prevent. Rounded up rather than to the nearest hundredth:
 * half a pixel of paint is still paint outside the box — and held against the
 * shipped files by the same suite that holds the Latin ratio, so the two cannot
 * drift apart from the faces they describe.
 */
export const FLASHBOOK_ARABIC_HAND_INK_RATIO = 1.44

/**
 * Every family the app ships — the four names Android resolves by file name and
 * iOS by internal family name.
 */
const BUNDLED_HANDS: readonly string[] = [
  LATIN.regular,
  LATIN.bold,
  FLASHBOOK_ARABIC_HANDS.regular,
  FLASHBOOK_ARABIC_HANDS.bold,
]

/**
 * The first family in a `fontFamily` value, unquoted.
 *
 * A web role names a stack, so the first name is the one the theme actually
 * chose; everything after it is a fallback the machine may or may not have.
 */
function primaryFamily(fontFamily: string): string {
  return fontFamily
    .split(',')[0]
    .trim()
    .replace(/^["']|["']$/g, '')
}

/**
 * Whether a `fontFamily` names one of the app's own hands.
 *
 * This is how the renderer tells "this role is *written*" apart from "this role
 * is set": only a written role has a face to re-resolve for the script it is
 * about to print, and re-stamping a set role would put a hand on a theme that
 * deliberately has none.
 */
export function isFlashBookHand(fontFamily?: string): boolean {
  if (!fontFamily) return false
  return BUNDLED_HANDS.includes(primaryFamily(fontFamily))
}

/**
 * The ink room a face needs, as a multiple of the type size.
 *
 * Per *hand* rather than per app: the two scripts draw different amounts above
 * and below the baseline, and this number is only ever used to grow a box that
 * clips, so answering with the wrong one is a clipped heading.
 */
export function flashBookHandInkRatio(fontFamily?: string): number {
  const family = fontFamily ? primaryFamily(fontFamily) : ''
  return family === FLASHBOOK_ARABIC_HANDS.regular ||
    family === FLASHBOOK_ARABIC_HANDS.bold
    ? FLASHBOOK_ARABIC_HAND_INK_RATIO
    : FLASHBOOK_HAND_INK_RATIO
}

/**
 * The hands a machine might already have, tried before the generic.
 *
 * Only ever consulted on the web, and only while the bundled face is still
 * loading — but they are what keeps that first frame handwriting instead of a
 * sans, so the order matters as much as the list.
 */
const PLATFORM_HANDS = [
  '"Bradley Hand"',
  '"Segoe Script"',
  '"Comic Sans MS"',
  '"Chalkboard SE"',
  '"Marker Felt"',
] as const

/**
 * The web stack for a weight, as a list rather than a joined string, written
 * left to right.
 *
 * Split out so the order is something a test can hold still: the bundled faces
 * first (a loaded face always wins), then whatever the machine has, then the
 * `cursive` generic last, so an unloaded font is a slightly different hand
 * rather than a different kind of type.
 *
 * This is the order for a book written left to right, and the only order the
 * *native* paths never see. Which bundled face leads is decided per direction by
 * `flashBookHandIn` — and it is not cosmetic, because the leading family is what
 * a browser lays the line's metrics out with.
 */
export function flashBookHandStack(fontWeight?: string | number): string[] {
  const bold = weightIsBold(fontWeight)
  return [
    bold ? LATIN.bold : LATIN.regular,
    bold ? FLASHBOOK_ARABIC_HANDS.bold : FLASHBOOK_ARABIC_HANDS.regular,
    ...PLATFORM_HANDS,
    'cursive',
  ]
}

/**
 * A theme's hand for a role of a given weight, as written in Latin.
 *
 * On the web that is the whole stack; on native it is the one bundled family,
 * because a list there would resolve to nothing at all. Defined through
 * `flashBookHandIn` so the two can never drift: this is the same answer, for the
 * script the theme tables are written in.
 */
export function flashBookHand(fontWeight?: string | number): string {
  return flashBookHandIn('ltr', fontWeight)
}

/**
 * The family a role should name for writing that runs this way.
 *
 * **Web** — the stack keeps every family, because a browser resolves a
 * `font-family` list *per glyph* and a right-to-left run quotes as well as
 * writes. But the *order* is not only about which glyphs are found: **the first
 * family is also the one that sets the line's metrics**, and the one
 * `flashBookHandInkRatio` reads to decide how much ink room a clamped heading
 * needs. A Latin face leading an Arabic heading is therefore not a harmless
 * preference — a browser worked through this the hard way, and both halves came
 * from that one ordering:
 *
 *  - The Latin face reports 22px of ascent over an 11px descent at 21px, so
 *    inside the notebook's 26px rule the baseline lands 18.5px down — while the
 *    Arabic hand draws 22.4px *above* that baseline. The heading's ink started
 *    3.9px above the box the clamp clips at, so the tops of the letters were
 *    shaved with no scrollbar and no ellipsis to show for it.
 *  - And the room reserved was read as the Latin 1.4 em rather than the Arabic
 *    1.44 em, so the box was a pixel short of what the face it was drawing with
 *    actually needs.
 *
 * Leading with the script's own face fixes both, because that face's metrics are
 * the ones the line is then laid out with — and the Arabic faces carry Latin
 * code points too, so an English word inside an Arabic heading stays in the same
 * hand rather than splitting the line across two scripts. Only the direction
 * decides; `direction` is set per block from the text itself (see
 * `#/lib/flashbookDirection`), which is the right granularity: an English
 * quotation printed on an Arabic page is written in the Latin hand, because it
 * is written in Latin.
 *
 * **iOS and Android** — a family is not a list and there is no per-glyph
 * fallback inside one, so the family has to be the one that *has* this script's
 * letters.
 */
export function flashBookHandIn(
  direction: FlashBookDirection | undefined,
  fontWeight?: string | number,
): string {
  const stack = flashBookHandStack(fontWeight)
  if (!IS_WEB) {
    const byWeight = direction === 'rtl' ? FLASHBOOK_ARABIC_HANDS : LATIN
    return weightIsBold(fontWeight) ? byWeight.bold : byWeight.regular
  }
  if (direction !== 'rtl') return stack.join(', ')
  const lead = weightIsBold(fontWeight)
    ? FLASHBOOK_ARABIC_HANDS.bold
    : FLASHBOOK_ARABIC_HANDS.regular
  return [lead, ...stack.filter(family => family !== lead)].join(', ')
}

function weightIsBold(fontWeight?: string | number): boolean {
  if (typeof fontWeight === 'number') return fontWeight >= 600
  if (!fontWeight) return false
  if (fontWeight === 'bold') return true
  const numeric = Number(fontWeight)
  if (!Number.isNaN(numeric)) return numeric >= 600
  return false
}

/**
 * The web faces, required so the bundler emits them.
 *
 * On native the same files are embedded by the `expo-font` plugin in
 * `app.config.js`, and this map is deliberately empty: there is nothing to load
 * and nothing to wait for, so a book never renders a frame in the wrong font.
 */
const WEB_FACES: Record<string, string | number> = IS_WEB
  ? {
      [LATIN.regular]: require('../../assets/fonts/flashbook-hand/FlashBookHand-Regular.woff2'),
      [LATIN.bold]: require('../../assets/fonts/flashbook-hand/FlashBookHand-Bold.woff2'),
      [FLASHBOOK_ARABIC_HANDS.regular]: require('../../assets/fonts/flashbook-hand/FlashBookHandArabic-Regular.woff2'),
      [FLASHBOOK_ARABIC_HANDS.bold]: require('../../assets/fonts/flashbook-hand/FlashBookHandArabic-Bold.woff2'),
    }
  : {}

/**
 * Load the hands the first time a book is rendered, and report whether they are
 * in yet.
 *
 * Loaded here rather than at app start because a reader who never opens a book
 * should not pay for its fonts — and reported rather than fire-and-forget so a
 * caller *can* hold a first paint if it ever wants to. Nothing depends on the
 * report today: the stack above already degrades to the machine's own hand, so
 * the worst case is a slightly different handwriting for a few frames.
 */
export function useFlashBookHands(): boolean {
  const [loaded] = useFonts(WEB_FACES)
  return loaded
}
